<template>
  <div
    v-if="loggedIn"
    :key="$route.path"
  >
    {{ errorContent }}
    <!-- Summary Statistics -->
    <b-row class="match-height mb-3">
      <b-col
        xl="3"
        md="6"
        sm="6"
      >
        <statistic-card-vertical
          color="warning"
          icon="FileTextIcon"
          :statistic="offerCount"
          statistic-title="Angebote Gesamt"
        />
      </b-col>
      <b-col
        xl="3"
        md="6"
        sm="6"
      >
        <statistic-card-vertical
          color="success"
          icon="CheckCircleIcon"
          :statistic="activeOfferCount"
          statistic-title="Aktive Angebote"
        />
      </b-col>
      <b-col
        xl="3"
        md="6"
        sm="6"
      >
        <statistic-card-vertical
          color="info"
          icon="DollarSignIcon"
          :statistic="`${activeOffersValue}€`"
          statistic-title="Aktiver Angebotswert"
        />
      </b-col>
      <b-col
        xl="3"
        md="6"
        sm="6"
      >
        <statistic-card-vertical
          color="primary"
          icon="TrendingUpIcon"
          :statistic="`${averageOfferValue}€`"
          statistic-title="⌀ Angebotswert"
        />
      </b-col>
    </b-row>
    
    <b-card no-body>
      <!-- Header mit Tabs, Per-Page und Suche -->
      <div class="m-2">
          
        <b-row>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start"
          >
            <label class="mr-1">Zeige</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-1"
              style="min-width: 80px; max-width: 100px;"
            />
            <label class="ml-1">Einträge</label>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="filter"
                class="d-inline-block mr-1"
                placeholder="Suche..."
                type="search"
              />
            </div>
          </b-col>
        </b-row>
          
        <b-tabs
          v-model="currentTab"
          class="mt-2 custom-tabs"
        >
          <b-tab :title="`Aktive Angebote (${activeCount})`" />
          <b-tab :title="`Inaktive Angebote (${inactiveCount})`" />
          <b-tab :title="`Angenommene Angebote (${acceptedCount})`" />
        </b-tabs>
      </div>

      <!-- Tabelle (Inhalt je nach Tab) -->
      <b-table
        ref="refListTable"
        striped
        hover
        :busy="loading"
        :current-page="currentPage"
        :per-page="perPage"
        :items="filteredOffers"
        :fields="fields"
        :filter="filter"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        responsive
        show-empty
        empty-text="Keine Einträge gefunden"
        @row-clicked="onRowClicked"
      >
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle mr-2" />
            <strong>Lade Angebotsdaten...</strong>
          </div>
        </template>
        <template #cell(storagesSum)="data">
          <b-badge
            variant="success"
          >
            {{ getOfferTotalValue(data.item) }} €
          </b-badge>
        </template>
        <template #cell(isActive)="data">
          <b-badge
            v-if="data.item.isActive"
            variant="success"
          >
            Ja
          </b-badge>
          <b-badge
            v-else
            variant="danger"
          >
            Nein
          </b-badge>
        </template>
        <template #cell(created_at)="data">
          {{ getFormattedDate(data.item.created_at) }}
        </template>
      </b-table>
    </b-card>

    <!-- Pagination -->
    <div class="mx-2 mb-2">
      <b-row>
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-start"
        >
          <span class="text-muted">
            Zeige {{ dataMeta.from }} bis {{ dataMeta.to }} von {{ getTotalRows }} Einträgen
          </span>
        </b-col>
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-end"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="getTotalRows"
            :per-page="perPage"
            first-number
            last-number
            prev-class="prev-item"
            next-class="next-item"
            class="mb-0"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
            </template>
            <template #next-text>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </b-pagination>
        </b-col>
      </b-row>
    </div>
  </div>
  <div v-else>
    Forbidden
  </div>
</template>

<script>
import {
  BCard,
  BTable,
  BCol,
  BRow,
  BBadge,
  BFormInput,
  BPagination,
  BTabs,
  BTab,
  BSpinner
} from 'bootstrap-vue'
import helper from '@/helper/helper'
import offerMetrics from '@/helper/offerMetrics'
import vSelect from 'vue-select'
import StatisticCardVertical from '@core/components/statistics-cards/StatisticCardVertical.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import tableHelper from '../../Table+Helper'

export default {
  setup() {
    const { getFormattedDate } = helper()
    const {
      perPageOptions,
      perPage,
      filter,
      totalRows,
      currentPage,
      refListTable,
      dataMeta,
      refetchData
    } = tableHelper()

    const {
      getOfferCount,
      getActiveOfferCount,
      getInactiveOfferCount,
      getOfferTotalValue,
      getActiveOffersValue,
      getAverageOfferValue,
      getNewestOffer,
      getHighestValueOffer
    } = offerMetrics()

    return {
      getFormattedDate,
      perPageOptions,
      filter,
      perPage,
      totalRows,
      currentPage,
      refListTable,
      dataMeta,
      refetchData,
      // Offer metrics
      getOfferCount,
      getActiveOfferCount,
      getInactiveOfferCount,
      getOfferTotalValue,
      getActiveOffersValue,
      getAverageOfferValue,
      getNewestOffer,
      getHighestValueOffer
    }
  },
  components: {
    BCard,
    BTable,
    BCol,
    BRow,
    BBadge,
    BPagination,
    BFormInput,
    BSpinner,
    vSelect,
    StatisticCardVertical,
    ToastificationContent,
    BTabs,
    BTab
  },
  data() {
    return {
      sortBy: 'id',
      sortDesc: true,
      // currentTab: 0 entspricht "Aktiv", 1 "Inaktiv", 2 "Angenommen"
      currentTab: 0,
      loading: false,
      errorContent: '',
      fields: [
        {
          key: 'id',
          label: 'ID',
          sortable: true
        },
        {
          key: 'isActive',
          label: 'Aktiv',
          sortable: true
        },
        {
          key: 'created_at',
          label: 'Erstellt',
          sortable: true
        },
        {
          key: 'firstName',
          label: 'Vorname',
          sortable: false
        },
        {
          key: 'lastName',
          label: 'Nachname',
          sortable: false
        },
        {
          key: 'email',
          label: 'Email',
          sortable: true
        },
        {
          key: 'phoneNumber',
          label: 'Telefon',
          sortable: true
        },
        {
          key: 'storages.length',
          label: '# Lager',
          sortable: true
        },
        {
          key: 'storagesSum',
          label: 'Angebotshöhe',
          sortable: true
        }
      ]
    }
  },

  computed: {
    // Alle Einträge aus dem Vuex-Store
    offersEntries() {
      const offers = this.$store.getters.offerEntries || []
      return [...offers].sort((a, b) => {
        const dateA = new Date(a.created_at)
        const dateB = new Date(b.created_at)
        return dateB.getTime() - dateA.getTime() // Newest first
      })
    },
    loggedIn() {
      return this.$store.state.authentication.status.loggedIn
    },
    // Filtert die Angebote je nach ausgewähltem Tab
    filteredOffers() {
      if (this.currentTab === 0) {
        // Active offers (isActive: true and not completed)
        return this.offersEntries.filter(offer => offer.isActive && !this.isOfferCompleted(offer))
      }
      if (this.currentTab === 1) {
        // Inactive offers (isActive: false and not completed)
        return this.offersEntries.filter(offer => !offer.isActive && !this.isOfferCompleted(offer))
      }
      // Accepted/completed offers
      return this.offersEntries.filter(offer => this.isOfferCompleted(offer))
    },
    // Gesamtanzahl der gefilterten Einträge für die Pagination
    getTotalRows() {
      return this.filteredOffers.length
    },
    // Anzahl aktiver Angebote
    activeCount() {
      return this.offersEntries.filter(offer => offer.isActive && !offer.bookingCompleted).length
    },
    // Anzahl inaktiver Angebote
    inactiveCount() {
      return this.offersEntries.filter(offer => !offer.isActive && !offer.bookingCompleted).length
    },
    // Anzahl angenommener Angebote
    acceptedCount() {
      return this.offersEntries.filter(offer => offer.bookingCompleted === true).length
    },
    // Statistics for cards
    offerCount() {
      return this.getOfferCount()
    },
    activeOfferCount() {
      return this.getActiveOfferCount()
    },
    activeOffersValue() {
      return this.getActiveOffersValue().toFixed(0)
    },
    averageOfferValue() {
      return this.getAverageOfferValue()
    },
  },
  watch: {
    currentTab() {
      // Reset pagination when switching tabs
      this.currentPage = 1
    }
  },
  async mounted() {
    await this.loadOffers()
  },
  updated() {
    this.totalRows = this.getTotalRows
  },
  methods: {
    // Check if an offer is completed
    isOfferCompleted(offer) {
      // Check new database field first
      if (offer.bookingCompleted === true) {
        return true
      }

      // Fallback: Check if there's an order referencing this offer (for existing data)
      if (offer.bookingCompleted === undefined || offer.bookingCompleted === null) {
        const orderEntries = this.$store.getters.orderEntries || []
        return orderEntries.some(order => order.offer_id === offer.id
          || order.offer === offer.id
          || (order.offer && order.offer.id === offer.id))
      }

      return false
    },
    async loadOffers() {
      this.loading = true
      this.errorContent = ''
      try {
        await this.$store.dispatch('getOfferEntries')
      } catch (error) {
        console.error('Error loading offers:', error)
        this.errorContent = 'Fehler beim Laden der Angebotsdaten. Bitte versuchen Sie es erneut.'
        this.toastNotification(
          'Angebotsdaten konnten nicht geladen werden.',
          'AlertTriangleIcon',
          'danger'
        )
      } finally {
        this.loading = false
      }
    },
    onRowClicked(row) {
      this.$router.push({
        name: 'offers-view',
        params: { id: row.id }
      })
    },
    toastNotification(title, icon, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          variant
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.custom-tabs {
  .nav-link {
    color: #3b3b3b;
    background-color: #f8f9fa;
    &.active {
      background-color: #fff;
      border-bottom-color: #fff;
    }
  }
  .nav-tabs {
    margin-bottom: 0px;
  }
}
</style>
